// src/pages/financials/BudgetTab.tsx
//
// Budget vs Actual — qbd-budget-vs-actual recipe + the agent's
// /api/qbd/budget direct endpoints.
//
// No budget yet → empty-state card with the Generate form (copy a base
// year's actuals forward with a % uplift). With a budget: KPI tiles
// (YTD actual/budget/variance), a net-income actual-bars vs budget-line
// chart, and per-section variance tables. Variances render red when
// UNFAVORABLE — income under budget, or expense-type sections over budget.
// Clicking an account row opens an editor modal with the 12 monthly budget
// cells (values from /api/qbd/budget, each saves on blur); Regenerate
// reopens the generate form (replaces the whole year). CSV export.
//
// Month labels are 'yyyy-MM' strings from the agent — rendered as-is.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Target, RefreshCw, Download, AlertTriangle, Wand2, Pencil, FileSpreadsheet,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdBudgetVsActualResponse, QbdBvaRow, QbdBvaSection } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import Modal from '../../components/Modal';

const fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

/** Whole-dollar currency; negatives in accounting parentheses. */
const amt = (v: number) => (v < 0 ? `(${fmt0.format(Math.abs(v))})` : fmt0.format(v));

/** Income-like sections are favorable when actual >= budget; expense-like the opposite. */
const isIncomeLike = (sectionKey: string) => /income/i.test(sectionKey);

/** True when the row's variance is unfavorable for its section. */
function isUnfavorable(sectionKey: string, r: QbdBvaRow): boolean {
  return isIncomeLike(sectionKey) ? r.actual < r.budget : r.actual > r.budget;
}

/** Map a BvA section key to the acctType the budget endpoints expect. */
const SECTION_ACCT_TYPE: Record<string, string> = {
  income: 'Income',
  cogs: 'CostOfGoodsSold',
  expense: 'Expense',
  otherIncome: 'OtherIncome',
  otherExpense: 'OtherExpense',
};

function exportBva(data: QbdBudgetVsActualResponse, exporter: typeof exportCsv = exportCsv) {
  interface FlatRow { label: string; actual: number | null; budget: number | null; variance: number | null; variancePct: number | null }
  const rows: FlatRow[] = [];
  for (const s of data.sections) {
    rows.push({ label: s.label, actual: null, budget: null, variance: null, variancePct: null });
    for (const r of s.rows) rows.push({ label: `  ${r.account}`, actual: r.actual, budget: r.budget, variance: r.variance, variancePct: r.variancePct });
    rows.push({ label: `Total ${s.label}`, actual: s.totals.actual, budget: s.totals.budget, variance: s.totals.variance, variancePct: s.totals.variancePct });
  }
  rows.push({
    label: 'Net Income (YTD)',
    actual: data.netIncome.actualYtd,
    budget: data.netIncome.budgetYtd,
    variance: data.netIncome.actualYtd - data.netIncome.budgetYtd,
    variancePct: null,
  });
  const cols: CsvColumns<FlatRow> = [
    ['Account', (r) => r.label],
    ['Actual', (r) => r.actual ?? ''],
    ['Budget', (r) => r.budget ?? ''],
    ['Variance', (r) => r.variance ?? ''],
    ['Variance %', (r) => r.variancePct != null ? r.variancePct.toFixed(1) : ''],
  ];
  exporter(`budget-vs-actual-${data.year}`, cols, rows);
}

export function BudgetTab() {
  const nowYear = new Date().getFullYear();
  const YEAR_OPTIONS = [nowYear, nowYear - 1, nowYear - 2];

  const [year, setYear] = useState(nowYear);
  const [regenOpen, setRegenOpen] = useState(false);
  const [editRow, setEditRow] = useState<{ account: string; sectionKey: string } | null>(null);

  const q = useQuery({
    queryKey: ['qbd', 'budget-vs-actual', year],
    queryFn: () => api.getQbdBudgetVsActual({ year }),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const sections = (data?.sections ?? []).filter((s) => s.rows.length > 0 || s.totals.actual !== 0 || s.totals.budget !== 0);

  const ytdVariance = data ? data.netIncome.actualYtd - data.netIncome.budgetYtd : 0;
  const ytdVariancePct = data && data.netIncome.budgetYtd !== 0
    ? (ytdVariance / Math.abs(data.netIncome.budgetYtd)) * 100
    : null;

  const chartData = (data?.months ?? []).map((m, i) => ({
    month: m,
    actual: data?.netIncome.actualByMonth[i] ?? 0,
    budget: data?.netIncome.budgetByMonth[i] ?? 0,
  }));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Target size={20} /> Budget
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Budget vs actual by account — generate from a base year, then tune per-account monthly amounts.
        </p>
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Year</span>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        {data?.hasBudget && (
          <button
            onClick={() => setRegenOpen(true)}
            title="Regenerate this year's budget from a base year (replaces every amount)"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
            <Wand2 size={11} /> Regenerate
          </button>
        )}
        <button
          onClick={() => data && exportBva(data)}
          disabled={!data || !data.hasBudget}
          title="Export budget vs actual to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> Export
        </button>
        <button
          onClick={() => data && exportBva(data, exportXlsx)}
          disabled={!data || !data.hasBudget}
          title="Export to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load budget vs actual</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && !data.hasBudget ? (
        /* Empty state: no budget for this year yet — generate one. */
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-6 max-w-lg">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-1">
            <Wand2 size={15} /> No budget for {year} yet
          </h3>
          <p className="text-xs text-slate-400 mb-4">
            Generate a starting budget by copying a base year's actuals forward with a percentage uplift,
            then fine-tune individual accounts month by month.
          </p>
          <GenerateForm year={year} onDone={() => { /* query invalidation refetches */ }} />
        </div>
      ) : !q.error && data && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
              <p className="text-xs text-slate-500">YTD Actual (Net Income)</p>
              <p className={`text-lg font-bold ${data.netIncome.actualYtd < 0 ? 'text-red-400' : 'text-white'}`}>{amt(data.netIncome.actualYtd)}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
              <p className="text-xs text-slate-500">YTD Budget (Net Income)</p>
              <p className={`text-lg font-bold ${data.netIncome.budgetYtd < 0 ? 'text-red-400' : 'text-white'}`}>{amt(data.netIncome.budgetYtd)}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
              <p className="text-xs text-slate-500">Variance $</p>
              <p className={`text-lg font-bold ${ytdVariance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {ytdVariance >= 0 ? '+' : ''}{amt(ytdVariance)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
              <p className="text-xs text-slate-500">Variance %</p>
              <p className={`text-lg font-bold ${ytdVariance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {ytdVariancePct != null ? fmtPct(ytdVariancePct) : '—'}
              </p>
            </div>
          </div>

          {/* Net income: actual bars vs budget line */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Net income by month — actual vs budget</h3>
            {chartData.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No months in the selected year</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value, name) => [fmt0.format(Number(value)), name === 'actual' ? 'Actual' : 'Budget']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                      formatter={(v) => v === 'actual' ? 'Actual' : 'Budget'} />
                    <ReferenceLine y={0} stroke="#475569" />
                    <Bar dataKey="actual" radius={[4, 4, 0, 0]} maxBarSize={32}>
                      {chartData.map((b) => (
                        <Cell key={b.month} fill={b.actual < 0 ? '#ef4444' : '#3b82f6'} />
                      ))}
                    </Bar>
                    <Line dataKey="budget" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2.5, fill: '#f59e0b' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Per-section variance tables */}
          {sections.map((s) => (
            <SectionTable key={s.key} section={s} onEditRow={(account) => setEditRow({ account, sectionKey: s.key })} />
          ))}
          {sections.length === 0 && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-6 text-center text-slate-500 text-sm">
              No budget or actual activity in {year}
            </div>
          )}
        </>
      )}

      {regenOpen && (
        <Modal title={`Regenerate ${year} budget`} onClose={() => setRegenOpen(false)}>
          <div className="space-y-3">
            <div className="p-3 rounded-md bg-amber-950/30 border border-amber-900/40 text-amber-200 text-xs flex items-center gap-2">
              <AlertTriangle size={14} className="shrink-0" />
              This replaces every budget amount for {year} — including any manual per-account edits.
            </div>
            <GenerateForm year={year} onDone={() => setRegenOpen(false)} />
          </div>
        </Modal>
      )}

      {editRow && (
        <BudgetRowEditor
          year={year}
          account={editRow.account}
          acctType={SECTION_ACCT_TYPE[editRow.sectionKey] ?? 'Expense'}
          months={data?.months ?? []}
          onClose={() => setEditRow(null)}
        />
      )}
    </div>
  );
}

// ── Section table ───────────────────────────────────────────────

function SectionTable({ section, onEditRow }: { section: QbdBvaSection; onEditRow: (account: string) => void }) {
  const varianceCell = (r: QbdBvaRow, bold = false) => {
    const bad = isUnfavorable(section.key, r);
    const cls = bad ? 'text-red-400' : 'text-emerald-400';
    return (
      <>
        <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${cls} ${bold ? 'font-semibold' : ''}`}>
          {r.variance >= 0 ? '+' : ''}{amt(r.variance)}
        </td>
        <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${cls} ${bold ? 'font-semibold' : ''}`}>
          {r.budget !== 0 ? fmtPct(r.variancePct) : '—'}
        </td>
      </>
    );
  };

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-700/50 bg-slate-800/50">
              <th className="px-3 py-2.5 text-left text-slate-400 font-medium min-w-[200px]">{section.label}</th>
              <th className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">Actual</th>
              <th className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">Budget</th>
              <th className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">Variance</th>
              <th className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">Var %</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((r) => (
              <tr key={r.account}
                onClick={() => onEditRow(r.account)}
                title="Click to edit this account's monthly budget"
                className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors cursor-pointer">
                <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    {r.account}
                    <Pencil size={10} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-slate-300 tabular-nums whitespace-nowrap">{amt(r.actual)}</td>
                <td className="px-3 py-2 text-right text-slate-400 tabular-nums whitespace-nowrap">{amt(r.budget)}</td>
                {varianceCell(r)}
              </tr>
            ))}
            <tr className="border-t border-slate-700/50 bg-slate-800/40">
              <td className="px-3 py-2 font-semibold text-white whitespace-nowrap">Total {section.label}</td>
              <td className="px-3 py-2 text-right font-semibold text-white tabular-nums whitespace-nowrap">{amt(section.totals.actual)}</td>
              <td className="px-3 py-2 text-right font-semibold text-slate-300 tabular-nums whitespace-nowrap">{amt(section.totals.budget)}</td>
              {varianceCell(section.totals, true)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Generate form (shared by the empty state and the Regenerate modal) ──

function GenerateForm({ year, onDone }: { year: number; onDone: () => void }) {
  const qc = useQueryClient();
  const BASE_OPTIONS = [year - 1, year - 2, year - 3];
  const [baseYear, setBaseYear] = useState(year - 1);
  const [upliftPct, setUpliftPct] = useState('5');

  const genM = useMutation({
    mutationFn: () => api.generateQbdBudget({ year, baseYear, upliftPct: Number(upliftPct) || 0 }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qbd'] });
      onDone();
    },
  });

  const inputCls = 'px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Base year (actuals)</span>
          <select value={baseYear} onChange={e => setBaseYear(Number(e.target.value))} className={inputCls}>
            {BASE_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Uplift %</span>
          <input type="number" step="0.5" value={upliftPct} onChange={e => setUpliftPct(e.target.value)}
            className={`${inputCls} w-20`} />
        </div>
        <button
          onClick={() => genM.mutate()}
          disabled={genM.isPending || upliftPct === '' || !Number.isFinite(Number(upliftPct))}
          className="px-3 py-1.5 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
          {genM.isPending ? <RefreshCw size={12} className="animate-spin" /> : <Wand2 size={12} />}
          Generate {year} budget
        </button>
      </div>
      {genM.error && (
        <div className="text-xs text-red-400">Couldn't generate: {(genM.error as Error).message}</div>
      )}
    </div>
  );
}

// ── Per-account monthly editor ──────────────────────────────────
// 12 month inputs seeded from /api/qbd/budget (filtered to the account);
// each cell saves on blur via putQbdBudgetCell. The BvA query is only
// invalidated when the modal CLOSES, so a burst of edits refetches once.

function BudgetRowEditor({ year, account, acctType, months, onClose }: {
  year: number; account: string; acctType: string; months: string[]; onClose: () => void;
}) {
  const qc = useQueryClient();
  // Fall back to the calendar year's 12 months if the recipe sent none.
  const ymList = months.length > 0 ? months : Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);

  const budgetQ = useQuery({
    queryKey: ['qbd', 'budget', year],
    queryFn: () => api.getQbdBudget(year),
    staleTime: 30_000,
    retry: 1,
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingYm, setSavingYm] = useState<string | null>(null);

  // Seed the inputs once the budget rows arrive.
  useEffect(() => {
    if (!budgetQ.data) return;
    const seeded: Record<string, string> = {};
    for (const ym of ymList) seeded[ym] = '0';
    for (const r of budgetQ.data) {
      if (r.account === account && seeded[r.ym] !== undefined) seeded[r.ym] = String(r.amount);
    }
    setValues(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetQ.data, account]);

  // Resolve the acctType from the stored budget rows when available (the
  // section-key mapping is only a fallback for accounts not yet in the table).
  const storedType = budgetQ.data?.find((r) => r.account === account)?.acctType;
  const effectiveType = storedType ?? acctType;

  const saveM = useMutation({
    mutationFn: (p: { ym: string; amount: number }) =>
      api.putQbdBudgetCell({ account, acctType: effectiveType, ym: p.ym, amount: p.amount }),
    onMutate: (p) => setSavingYm(p.ym),
    onSuccess: () => { setSaveError(null); qc.invalidateQueries({ queryKey: ['qbd', 'budget', year] }); },
    onError: (e) => setSaveError((e as Error).message),
    onSettled: () => setSavingYm(null),
  });

  function handleBlur(ym: string) {
    const raw = values[ym];
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) return;
    const original = budgetQ.data?.find((r) => r.account === account && r.ym === ym)?.amount ?? 0;
    if (n === original) return;
    saveM.mutate({ ym, amount: n });
  }

  function close() {
    // One refetch for the whole editing session.
    qc.invalidateQueries({ queryKey: ['qbd', 'budget-vs-actual', year] });
    onClose();
  }

  const total = ymList.reduce((sum, ym) => sum + (Number(values[ym]) || 0), 0);

  return (
    <Modal title={`Budget · ${account}`} onClose={close} panelClassName="max-w-sm">
      <div className="space-y-3">
        <p className="text-xs text-slate-500 -mt-3">
          {year} monthly amounts · {effectiveType} · each cell saves when you leave it
        </p>
        {budgetQ.error && (
          <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
            <AlertTriangle size={14} /> Couldn't load the budget: {(budgetQ.error as Error).message}
          </div>
        )}
        {budgetQ.isLoading ? (
          <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {ymList.map((ym) => (
                <div key={ym} className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400 tabular-nums w-14 shrink-0">{ym}</span>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      step="0.01"
                      value={values[ym] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [ym]: e.target.value }))}
                      onBlur={() => handleBlur(ym)}
                      className="w-full px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                    />
                    {savingYm === ym && (
                      <RefreshCw size={10} className="absolute -left-1.5 top-1/2 -translate-y-1/2 -translate-x-full text-blue-400 animate-spin" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-700/40">
              <span className="text-xs text-slate-400">Year total</span>
              <span className="text-sm font-semibold text-white tabular-nums">{amt(total)}</span>
            </div>
          </>
        )}
        {saveError && <div className="text-xs text-red-400">Couldn't save: {saveError}</div>}
        <div className="flex justify-end">
          <button onClick={close}
            className="px-3 py-1.5 text-xs rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40">
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
